import { Pencil } from "lucide-react";
import { Tip, Bars } from "@/components/c360/C360Table";
import { RATING } from "@/lib/creator360";
import { GRADE, TAG, VIEW_TAB, day, rankLine, reason, tgUnread } from "@/lib/c360Profile";
import { alsoItem, neutralTag, tiles } from "@/lib/c360Tiles";
import { CYAN, changedLine, rWord } from "@/lib/c360Mine";

export function gradeWord(t) {
  if (t.keeps) return { word: "Keeps it Good", color: GRADE.good.color };
  if (t.makes && t.grade === "weak") return { word: "Makes it Weak", color: GRADE.weak.color };
  return t.grade ? GRADE[t.grade] : null;
}

// One check tile. A check the BD changed (`mc`) shows their grade in cyan; KOL Radar's stays in the hover.
export function CheckTile({ t, av, compact, mc, m }) {
  const gw = mc ? { word: `${GRADE[mc].word} · your call`, color: CYAN } : gradeWord(t);
  const red = t.k === "bot";
  const hover = mc ? (
    <div className="space-y-2">
      <p className="text-cyan-300" data-testid={`c360-tile-${av}-${t.k}-mine`}>You changed this check to {GRADE[mc].word} on {day(m.at)} (KOL Radar: {GRADE[t.grade]?.word || "Not judged"}).{m.note ? ` Your note: "${m.note}".` : ""}</p>
      <p>{t.hover}</p>
    </div>
  ) : t.hover;
  const border = mc ? `${CYAN}88` : red ? "#F43F5E66" : t.keeps ? "#FBBF2466" : "rgba(255,255,255,0.08)";
  const bg = mc ? "rgba(0,240,255,0.06)" : red ? "rgba(244,63,94,0.08)" : t.keeps ? "rgba(251,191,36,0.06)" : "rgba(255,255,255,0.02)";
  return (
    <Tip wide testid={`c360-tile-${av}-${t.k}`} content={hover}>
      <span data-grade={t.grade || "none"} data-mine={mc || undefined} className={`block rounded-md border ${compact ? "px-2.5 py-2" : "px-3 py-2.5"} h-full`} style={{ borderColor: border, background: bg }}>
        <span className="block text-[11px] text-zinc-400 truncate">{t.name}</span>
        <span className={`block text-white font-semibold ${compact ? "text-sm" : "text-base"} mt-0.5 truncate`} data-testid={`c360-tile-${av}-${t.k}-value`}>{t.value}</span>
        {!compact && t.sub && <span className="block mono text-[10px] text-zinc-500 truncate">{t.sub}</span>}
        {gw && <span className="block mono text-[10px] uppercase tracking-wider mt-1" style={{ color: gw.color }} data-testid={`c360-tile-${av}-${t.k}-grade`}>● {gw.word}</span>}
      </span>
    </Tip>
  );
}

export function TileRow({ av, d, compact }) {
  const ts = tiles(av, d);
  const m = d.mine?.[av];
  if (!ts.length) return null;
  return (
    <div className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 md:grid-cols-4"}`} data-testid={`c360-tiles-${av}`}>
      {ts.map((t) => <CheckTile key={t.k} t={t} av={av} compact={compact} mc={m?.checks?.[t.k]} m={m} />)}
    </div>
  );
}

export function EditButton({ onClick, testid }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} title="Change the verdict" aria-label="Change the verdict" data-testid={testid}
      className="h-7 w-7 inline-flex items-center justify-center rounded-md border hairline text-zinc-400 hover:text-cyan-300 hover:border-cyan-400/50 transition-colors"><Pencil size={12} /></button>
  );
}

export function YourCall({ m, kol, testid }) {
  return (
    <div className="mt-3 rounded-md border px-4 py-3 text-sm text-cyan-100" style={{ borderColor: `${CYAN}66`, background: "rgba(0,240,255,0.05)" }} data-testid={testid}>
      <p><span className="font-semibold text-cyan-300">Your call.</span> This is not KOL Radar's verdict: {changedLine(m, kol)}. KOL Radar's own reading is just above.</p>
      {m.note && <p className="mt-1.5 text-cyan-200/80" data-testid={`${testid}-note`}>Your note: "{m.note}"</p>}
    </div>
  );
}

const tint = (r) => (r === "unknown" ? { borderColor: "rgba(113,113,122,0.4)", background: "rgba(113,113,122,0.06)" } : { borderColor: `${RATING[r].color}55`, background: `${RATING[r].color}0c` });
const CYAN_TINT = { borderColor: `${CYAN}66`, background: "rgba(0,240,255,0.04)" };

function Also({ av, also }) {
  return (
    <div className="mt-4 text-xs text-zinc-400" data-testid={`c360-also-${av}`}>
      <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-2">Also</span>
      <Tip wide content={also.hover} testid={`c360-also-${av}-item`}>
        <span className="underline decoration-dotted underline-offset-4 text-zinc-200">{also.name} {also.value}{also.sub ? ` (${also.sub})` : ""}{also.r != null ? ` (${neutralTag(also.r).toLowerCase()})` : ""}</span>
      </Tip>
      {also.tail && <span className="ml-1">· {also.tail}</span>}
    </div>
  );
}

export function VerdictBox({ av, d, onEdit }) {
  const x = d.av[av];
  const m = d.mine?.[av];
  const mr = m?.r;
  const R = RATING[mr || x.r];
  const color = mr ? CYAN : R.color;
  const also = alsoItem(av, d, d.groups[av]);
  const tag = mr ? "Your call · not KOL Radar's verdict" : av === "tg" && tgUnread(d) ? TAG.good : TAG[x.r];
  const notes = tiles(av, d).filter((t) => t.note);
  const why = reason(av, d);
  return (
    <section className="rounded-lg border p-5 c360-rise" style={mr ? CYAN_TINT : tint(x.r)} data-testid={`c360-verdict-${av}`} data-rating={x.r} data-mine={mr || undefined}
      onContextMenu={(e) => { e.preventDefault(); onEdit(av); }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-baseline gap-x-3">
          <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Verdict · {VIEW_TAB[av]}</span>
          <span className={`text-[11px] ${m ? "text-cyan-300/70" : "text-zinc-600"}`} data-testid={`c360-hint-${av}`}>{m ? "changed by you · right-click to edit" : "KOL Radar's verdict · right-click to change"}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="mono text-[11px] text-zinc-400" data-testid={`c360-rank-${av}`}>{rankLine(av, d)}</span>
          <EditButton onClick={() => onEdit(av)} testid={`c360-edit-${av}`} />
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <span className="text-2xl font-semibold inline-flex items-center gap-2" style={{ color }} data-testid={`c360-rating-${av}`}><Bars rating={mr || x.r} size={16} color={mr ? CYAN : undefined} />{R.label}</span>
        {tag && <span className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded" style={mr ? { color: CYAN, border: `1px solid ${CYAN}88` } : { color, background: `${color}14` }} data-testid={`c360-tag-${av}`}>{tag}</span>}
      </div>
      <p className={`mt-3 text-sm leading-relaxed ${x.r === "unknown" ? "text-zinc-400" : "text-zinc-200"} max-w-4xl`} data-testid={`c360-reason-${av}`}>{mr ? `KOL Radar's reading (${rWord(x.r)}): ${why}` : why}</p>
      {mr && <YourCall m={m} kol={x.r} testid={`c360-yourcall-${av}`} />}
      {x.r !== "unknown" && (
        <>
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-5 mb-2">What decides the verdict</div>
          <TileRow av={av} d={d} />
          {notes.map((t) => <p key={t.k} className="text-xs text-amber-300/80 mt-2" data-testid={`c360-note-${av}-${t.k}`}>{t.note}</p>)}
          {also && <Also av={av} also={also} />}
        </>
      )}
    </section>
  );
}
